import { useEffect, useState } from 'react';
import { SubscriptionApi } from '../api/SubscriptionApi';

export interface SubscriptionStatus {
  isActive: boolean;            // ACTIVE or INFINITE
  isLoading: boolean;
  status: string | null;        // raw status string from server
  remainingDays: number | null;
}

/**
 * Lightweight hook that fetches the current user's subscription status.
 * Results are cached in module-level variables so multiple components
 * don't each fire a separate request within the same page lifecycle.
 */
let _cachedStatus: SubscriptionStatus | null = null;
let _fetchPromise: Promise<void> | null = null;

export function useSubscriptionStatus(): SubscriptionStatus {
  const [state, setState] = useState<SubscriptionStatus>(
    _cachedStatus ?? { isActive: true, isLoading: true, status: null, remainingDays: null }
  );

  useEffect(() => {
    if (_cachedStatus) {
      setState(_cachedStatus);
      return;
    }

    if (!_fetchPromise) {
      _fetchPromise = SubscriptionApi.getMySubscription()
        .then(res => {
          const data = res.data;
          const active = data
            ? (data.infiniteSubscription || (data.status === 'ACTIVE' && new Date(data.endDate).getTime() > Date.now()))
            : true; // no subscription record → treat as active (grace)
          _cachedStatus = {
            isActive: active,
            isLoading: false,
            status: data?.status ?? null,
            remainingDays: data?.remainingDays ?? null,
          };
        })
        .catch(() => {
          // On error treat as active so the UI doesn't wrongly block users
          _cachedStatus = { isActive: true, isLoading: false, status: null, remainingDays: null };
        });
    }

    _fetchPromise.then(() => {
      if (_cachedStatus) setState(_cachedStatus);
    });
  }, []);

  return state;
}

/** Call this when the user logs out so the cache is cleared. */
export function clearSubscriptionCache() {
  _cachedStatus = null;
  _fetchPromise = null;
}
